
/* eslint-disable react/destructuring-assignment */
import React, { useEffect, useMemo, useState } from 'react';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import * as reportActions from '../../../../core/report/reportActions';
import * as globalActions from '../../../../core/global/globalActions';
import Layout from '../../../../components/layout/Layout';
import FilterSidebar from '../../../../components/FilterSidebar';
import GridView from '../../../../components/elements/GridView/index';
import ReportFilter, { DATA_MODE_ASSETS_OR_DIVISIONS, DATE_MODE_RANGE } from '../../ReportFilter';
import dayjs from 'dayjs';
const advancedFormat = require("dayjs/plugin/advancedFormat");
dayjs.extend(advancedFormat);

import { isEmpty } from 'underscore';
import CustomEmpty, { CustomEmptyType } from '../../../../components/CustomEmpty';
import { RootState } from '../../../../core/reducers';
import { getPaginationTotal } from '../../../../components/elements/GridView/GridViewBase/Pagination';

import './dvrHealthAlarmsReport.scss';
import { CheckmarkFilled, CloseFilled, WarningAltFilled } from '@carbon/icons-react';

const { REPORT_TYPES, DB_DATE_FORMAT, USER_DATETIME_FORMAT, PAGE_SIZE_OPTIONS } = require('../../../../core/constants').default;

const performDvrHealthAlarmsReportSearch = (
    values: any,
    actions: any,
    setFilters: React.Dispatch<any>,
) => {
    const filters = values;
    if (filters.date_from) filters.date_from = dayjs(values.date_from).format(DB_DATE_FORMAT);
    if (filters.date_to) filters.date_to = dayjs(values.date_to).format(DB_DATE_FORMAT);

    if (!filters?.page) {
        filters.page = 1;
    }
    setFilters(filters);
    actions.getDvrHealthAlarmsReportRequest({ ...filters });
};

const onPageChange = (
    page: number,
    per_page: number,
    sorter: any,
    filters: any,
    actions: any,
    setCurrentPage: React.Dispatch<any>,
    setFilters: React.Dispatch<any>
) => {
    const order = sorter?.order;
    const sort_by = order ? sorter.field : undefined;
    if (filters?.order !== order || filters?.sort_by !== sort_by) {
        page = 1;
    }

    setCurrentPage(page);
    const newFilter = { ...filters, page, per_page, order, sort_by };
    performDvrHealthAlarmsReportSearch(newFilter, actions, setFilters);
}

interface DvrHealthAlarmsReportProps { }

const DvrHealthAlarmsReport: React.FC<DvrHealthAlarmsReportProps> = () => {
    const [filters, setFilters] = useState<any>({});
    const [currentPage, setCurrentPage] = React.useState(1);

    const user: any = useSelector((state: RootState) => state.user);
    const isFetching: boolean = useSelector((state: RootState) => state.report.isFetching);
    const dvrHealthAlarmsReport: any = useSelector((state: RootState) => state.report.dvrHealthAlarmsReport);
    const dvrHealthAlarmsReportPagination: any = useSelector((state: RootState) => state.report.dvrHealthAlarmsReportPagination);
    const dispatch = useDispatch();
    const actionOptions: any = {
        ...reportActions,
        ...globalActions,
    };
    const actions: any = useMemo(() => bindActionCreators(
        actionOptions,
        dispatch,
    ), []);

    useEffect(() => {
        actions.resetReportsRequest();
        setCurrentPage(1);
        const params = {
            page: 1,
        };
        performDvrHealthAlarmsReportSearch(params, actions, setFilters);
    }, []);

    const listReports = dvrHealthAlarmsReport.map((log) => {
        let status = (<WarningAltFilled
            size={16}
            fill='#EFB700'
        />);
        if (log.status === 'failed') {
            status = <CloseFilled
                size={16}
                fill='#B81D13'
            />;
        } else if (log.status === 'success') {
            status = <CheckmarkFilled
                size={16}
                fill='#008450'
            />;
        }

        return {
            "status": (<div style={{ minWidth: '50px', paddingLeft: '12px' }} > {status}</div >),
            "timestamp": dayjs(log.alarm_at).format(USER_DATETIME_FORMAT),
            "health_notification": log.alert_text,
            "asset": log.asset_name,
            "division": log.division_name,
        };
    });

    const customEmptyComponent = (
        <CustomEmpty
            type={CustomEmptyType.Report}
            showIcon
        />
    );

    return (
        <Layout
            className="dvr-health-report"
            title={<div>DVR Health Report</div>}
            headerTitle={<div>DVR Health Report</div>}
            loading={isFetching}
        >
            <div className={`content-wrap${user?.userCompany?.current_alert ? '-with-banner' : ''}`}>
                <FilterSidebar displayFilter>
                    <ReportFilter
                        onSubmit={(filters) => {
                            setCurrentPage(1);
                            actions.resetReportsRequest();
                            const newFilter = { ...filters, page: 1 };
                            performDvrHealthAlarmsReportSearch(newFilter, actions, setFilters);
                        }}
                        filters={filters}
                        dataMode={DATA_MODE_ASSETS_OR_DIVISIONS}
                        dateMode={DATE_MODE_RANGE}
                        showDivision
                        showDvrHealthAlarmsType
                        showDvrHealthReportsType
                        reportType={REPORT_TYPES.find((rt) => rt.link === 'dvr-health-report').link}
                    />
                </FilterSidebar>
                <div
                    className="content"
                    style={{ opacity: isFetching ? 0 : 1 }}
                >
                    <div className="content-inner">
                        <section className="section">
                            <div className="section__body">
                                <span className='report-title'>DVR Health Report</span>
                                {isEmpty(listReports) || listReports.length < 1 ? customEmptyComponent : (
                                    <>
                                        <GridView
                                            data={listReports}
                                            onChange={(pagination, pagFilters, sorter, extra) => {
                                                onPageChange(
                                                    pagination.current,
                                                    pagination.pageSize,
                                                    sorter,
                                                    filters,
                                                    actions,
                                                    setCurrentPage,
                                                    setFilters
                                                )
                                            }}
                                            sortableColumns={['asset', 'division', 'timestamp', 'health_notification']}
                                            hiddenColumns={['id', 'dvr_health_report_id', 'alert_type', 'alarm_id']}
                                            pagination={{
                                                total: dvrHealthAlarmsReportPagination.totalRecords,
                                                pageSize: dvrHealthAlarmsReportPagination.perPageCount,
                                                showSizeChanger: true,
                                                pageSizeOptions: PAGE_SIZE_OPTIONS,
                                                showTotal: getPaginationTotal,
                                                current: currentPage,
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default DvrHealthAlarmsReport;
